import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DEFAULT_REGION,
  latitudeDeltaToZoom,
} from "#app/components/map/map-styles";
import type { Region } from "#app/components/map/map-types";
import type { BoundsInput } from "#app/lib/data";

// onRegionChangeComplete already fires only when the map settles,
// so the debounce just needs to collapse rapid-fire settle events
// (e.g. programmatic animateToRegion followed by user gesture).
const DEFAULT_DEBOUNCE_MS = 30;
const DEFAULT_BUFFER = 0.2;

/**
 * Shared hook for debounced, buffered map bounds management.
 *
 * Encapsulates three behaviors:
 * 1. Debounced region-to-bounds conversion
 * 2. Bounds buffer (fetches wider than viewport to absorb small pans)
 * 3. Containment check (skips query when viewport is inside previously-fetched bounds)
 */
export function useBufferedBounds(options?: {
  debounceMs?: number;
  buffer?: number;
}) {
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const buffer = options?.buffer ?? DEFAULT_BUFFER;

  const [region, setRegion] = useState<Region>(DEFAULT_REGION);
  const [bounds, setBounds] = useState<BoundsInput | null>(null);

  const lastQueryRef = useRef<{ bounds: BoundsInput; zoom: number } | null>(
    null,
  );
  const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(boundsTimerRef.current), []);

  const handleRegionChange = useCallback(
    (newRegion: Region) => {
      clearTimeout(boundsTimerRef.current);
      boundsTimerRef.current = setTimeout(() => {
        const currentZoom = Math.round(
          latitudeDeltaToZoom(newRegion.latitudeDelta),
        );

        const viewNorth = newRegion.latitude + newRegion.latitudeDelta / 2;
        const viewSouth = newRegion.latitude - newRegion.latitudeDelta / 2;
        const viewEast = newRegion.longitude + newRegion.longitudeDelta / 2;
        const viewWest = newRegion.longitude - newRegion.longitudeDelta / 2;

        const last = lastQueryRef.current;
        const zoomChanged = !last || last.zoom !== currentZoom;
        const outOfBounds =
          !last ||
          viewNorth > last.bounds.north ||
          viewSouth < last.bounds.south ||
          viewEast > last.bounds.east ||
          viewWest < last.bounds.west;

        setRegion(newRegion);

        if (zoomChanged || outOfBounds) {
          const latHalf = newRegion.latitudeDelta / 2;
          const lngHalf = newRegion.longitudeDelta / 2;
          const newBounds: BoundsInput = {
            north: newRegion.latitude + latHalf * (1 + buffer),
            south: newRegion.latitude - latHalf * (1 + buffer),
            east: newRegion.longitude + lngHalf * (1 + buffer),
            west: newRegion.longitude - lngHalf * (1 + buffer),
          };
          lastQueryRef.current = { bounds: newBounds, zoom: currentZoom };
          setBounds(newBounds);
        }
      }, debounceMs);
    },
    [debounceMs, buffer],
  );

  /** Clear the containment cache so the next region change always fires a fresh query. */
  const reset = useCallback(() => {
    lastQueryRef.current = null;
  }, []);

  const zoomLevel = useMemo(
    () => latitudeDeltaToZoom(region.latitudeDelta),
    [region.latitudeDelta],
  );

  return { bounds, region, zoomLevel, handleRegionChange, reset };
}
